/* eslint-disable react-hooks/exhaustive-deps */
import {
  Column,
  Highlight,
  ReactGrid,
  ReactGridProps,
} from "@silevis/reactgrid";
// import "@silevis/reactgrid/styles.css";
import { BUTTON_HEIGHT, FONT_SIZE, ROUTES } from "@lhd/constants";
import Decimal from "decimal.js";
import { get, toString } from "lodash";
import debounce from "lodash/debounce";
import {
  forwardRef,
  Fragment,
  Component,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { getDisplaySize } from "../DisplaySizeSelector/selector";
import CellInfoSum from "./CellInfoSum";
import FilterHeader from "./FilterHeader";
import { HeaderIconCellTemplate } from "./FilterHeader/CellTemplate";
import HeaderGroup from "./HeaderGroup";
import TooltipHeader from "./Tooltip";
import { useAutoRowHeight } from "./hooks/useCalHeightRow";
import PanelCtrlH from "./panelCtrlH";
import { Styles } from "./style";
import { parseColumnId, sumAllColumns } from "./utils";

interface ColumnProps extends Column {
  type?: string;
  label?: string;
  editTable?: any;
  icon?: React.ReactNode;
  required?: boolean;
  tooltip?: string;
  hiddenFilter?: boolean;
}
interface ReactGridCustomProps extends ReactGridProps {
  reactGridRef?: React.RefObject<ReactGrid> | null;
  columns: ColumnProps[];
  additionalField?: string[];
  dateFieateHierarchyFields?: string[];
  requiredFields?: string[];
  formik?: any;
  data?: any[];
  originalData?: any[];
  onFilter?: (props?: { isClearFilter: boolean }) => void;
  elementRef?: React.RefObject<HTMLDivElement>;
  wrapContent?: React.ReactElement;
  stylesTableContainer?: React.CSSProperties;
  isSpecialRowHeader?: boolean;
  filterFields?: string[];
  getValue?: (value: string, type: string) => string;
  isPreferUseGetValue?: boolean;
  addHeight?: number;
  isShowTooltip?: boolean;
  isHiddenTotalRow?: boolean;
  heightTable?: string | number;
  maxHeight?: string;
  className?: string;
  isCtrlHToReplacesValue?: boolean;
  nameTable?: string;
  onTableScroll?: any;
  isAutoHeight?: boolean;
  fetchFilterOptions?: (columnId: string) => Promise<{
    options: { value: string; label: string }[];
    isShowEmptyDate: boolean;
  }>;
}

type ActiveGridController = {
  id: string;
  deactivate: () => void;
};

let activeGridController: ActiveGridController | null = null;

const activateGridController = (nextController: ActiveGridController) => {
  if (activeGridController && activeGridController.id !== nextController.id) {
    activeGridController.deactivate();
  }
  activeGridController = nextController;
};

const clearGridController = (id: string) => {
  if (activeGridController?.id === id) {
    activeGridController = null;
  }
};

class GridErrorBoundary extends Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = { errorKey: 0, hasError: false };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true };
  }
  componentDidCatch(error: any, errorInfo: any) {
    console.error("ReactGrid internal crash caught, forcing remount...", error);
    this.setState({ errorKey: this.state.errorKey + 1, hasError: false });
  }
  render() {
    if (this.state.hasError) {
      return null;
    }
    return <Fragment key={this.state.errorKey}>{this.props.children}</Fragment>;
  }
}

const ReactGirdCustom = forwardRef<ReactGrid, ReactGridCustomProps>(
  (props, forwardedRef) => {
    const {
      reactGridRef,
      addHeight,
      isHiddenTotalRow,
      isAutoHeight,
      fetchFilterOptions,
    } = props;
    const [highlightedRows, setHighlightedRows] = useState<Highlight[]>([]);
    const internalRef = useRef<ReactGrid>(null);
    const [ranges, setRanges]: any = useState<
      ReactGrid["state"]["selectedRanges"]
    >([]);
    const NUMBER_TYPES = [
      "numberCurrency",
      "numberformat",
      "number",
      "record_number",
      "numberFormat",
    ];

    const isAllNumberColumns = ranges.every(
      (range: any) =>
        range.columns?.every((column: any) => {
          const appPartType = column.appPart?.app_part_type;

          return (
            NUMBER_TYPES.includes(column.type) ||
            NUMBER_TYPES.includes(appPartType)
          );
        }) ?? false,
    );

    const {
      handleCalHeightByCellChange,
      handleCalHeightByRowIds,
      configRowHeights,
    } = useAutoRowHeight({ isAutoHeight });

    const gridContainerRef = useRef<HTMLDivElement | null>(null);
    const gridInstanceIdRef = useRef(
      `react-grid-${Math.random().toString(36).slice(2, 11)}`,
    );

    const [filterInfo, setFilterInfo] = useState<{
      open: boolean;
      columnId: string;
      position?: {
        x?: number;
        y?: number;
      };
    }>({
      open: false,
      columnId: "",
    });

    const scrollToCell = (cell: { rowId: string; columnId: string }) => {
      const scrollableElement = ref.current?.state?.scrollableElement as
        | HTMLDivElement
        | undefined;
      const reactGridElement = ref.current?.state?.reactGridElement as
        | HTMLDivElement
        | undefined;
      if (!scrollableElement || !reactGridElement) return;
      const rowIndex = rows.findIndex(
        (row) => String(row.rowId) === String(cell.rowId),
      );
      const columnIndex = props.columns.findIndex(
        (column) => String(column.columnId) === String(cell.columnId),
      );
      if (rowIndex < 0 || columnIndex < 0) return;
      const cellElement = reactGridElement.querySelector(
        `.rg-cell[data-cell-rowidx="${rowIndex}"][data-cell-colidx="${columnIndex}"]`,
      ) as HTMLElement | null;
      if (cellElement) {
        const cellRect = cellElement.getBoundingClientRect();
        const scrollRect = scrollableElement.getBoundingClientRect();
        const paddingX = 24;
        const paddingTop = 72;
        const paddingBottom = 72;
        const paddingRight = 24;
        const topLimit = scrollRect.top + paddingTop;
        const bottomLimit = scrollRect.bottom - paddingBottom;
        const leftLimit = scrollRect.left + paddingX;
        const rightLimit = scrollRect.right - paddingRight;
        let deltaTop = 0;
        let deltaLeft = 0;
        if (cellRect.top < topLimit) {
          deltaTop = cellRect.top - topLimit;
        } else if (cellRect.bottom > bottomLimit) {
          deltaTop = cellRect.bottom - bottomLimit;
        }
        if (cellRect.left < leftLimit) {
          deltaLeft = cellRect.left - leftLimit;
        } else if (cellRect.right > rightLimit) {
          deltaLeft = cellRect.right - rightLimit;
        }
        if (deltaTop === 0 && deltaLeft === 0) {
          return;
        }
        scrollableElement.scrollTo({
          top: Math.max(0, scrollableElement.scrollTop + deltaTop),
          left: Math.max(0, scrollableElement.scrollLeft + deltaLeft),
        });
        return;
      }
      const topOffset = rows
        .slice(0, rowIndex)
        .reduce((acc, row) => acc + (row.height ?? 0), 0);
      const leftOffset = props.columns
        .slice(0, columnIndex)
        .reduce((acc, column) => acc + (column.width ?? 0), 0);
      scrollableElement.scrollTo({
        top: Math.max(0, topOffset - 40),
        left: Math.max(0, leftOffset - 40),
      });
    };

    const [shadowTopElement, setShadowTopElement] =
      useState<HTMLElement | null>(null);

    const ref =
      reactGridRef ||
      (forwardedRef as React.RefObject<ReactGrid>) ||
      internalRef;

    const sums = sumAllColumns(ranges);
    let total = new Decimal(0);
    Object.values(sums.total).forEach((x) => (total = total.add(x)));
    const style = { left: 0, top: 0 };
    if (ref.current?.state?.scrollableElement) {
      const scrollableElement = ref.current?.state
        ?.scrollableElement as HTMLDivElement;
      const rect = scrollableElement.getBoundingClientRect();
      style.left = rect.left;
      style.top = rect.bottom;
    }
    const format = Intl.NumberFormat("ja-JP").format;
    const isShow =
      ranges.length > 0 && sums.countNumber > 1 && isAllNumberColumns;

    //for filter panel

    const displaySize = useSelector(getDisplaySize) as
      | keyof typeof FONT_SIZE
      | keyof typeof BUTTON_HEIGHT;
    const heightHeaderRow = BUTTON_HEIGHT[displaySize] ?? BUTTON_HEIGHT.small;

    const isCustomHeader =
      location.pathname.includes(ROUTES.ROLE_PERMISSIONS) ||
      /^\/annual_salary_summaries\/\d{4}\/provisions$/.test(
        location.pathname,
      ) ||
      /^\/annual_salary_summaries\/\d{4}\/works$/.test(location.pathname) ||
      /^\/annual_salary_summaries\/\d{4}\/staff_tax_sheets$/.test(
        location.pathname,
      ) ||
      /^\/annual_salary_summaries\/\d{4}\/expenses$/.test(location.pathname) ||
      /^\/annual_salary_summaries\/\d{4}\/deductions$/.test(
        location.pathname,
      ) ||
      /^\/annual_salary_summaries\/\d{4}\/arrangements$/.test(
        location.pathname,
      ) ||
      /^\/annual_salary_summaries\/\d{4}\/deductions$/.test(
        location.pathname,
      ) ||
      /^\/annual_salary_summaries\/\d{4}\/transfers$/.test(location.pathname) ||
      /^\/companies\/create$/.test(location.pathname) ||
      /^\/apps\/\d+\/records\/app-sheets$/.test(location.pathname) ||
      location.pathname.includes(`${ROUTES.WORKERS}/${ROUTES.WORKER_SHEETS}`) ||
      location.pathname.includes(
        `${ROUTES.CUSTOMER_LIST}/${ROUTES.CUSTOMER_SHEET}`,
      ) ||
      /^\/customers\/create$/.test(location.pathname);

    const scrollableElement = ref.current?.state?.scrollableElement as
      | HTMLDivElement
      | undefined;

    const isHorizontalScrollDisabled =
      props.rows.length === 1 ||
      (scrollableElement
        ? scrollableElement.scrollWidth <= scrollableElement.clientWidth
        : true);

    const heightRow = isCustomHeader
      ? heightHeaderRow + (!isHorizontalScrollDisabled ? 0 : 0)
      : 45;

    const combineRowWithHeightConfig = (
      rows: typeof props.rows,
      configRowHeights: Record<string, number>,
    ) => {
      if (!isAutoHeight) return rows;
      return rows.map((row) => {
        const { rowId } = row;
        const height = configRowHeights[rowId] || row.height || 45;
        return {
          ...row,
          height,
        };
      });
    };

    const { rows, groupHeaderRow } = useMemo(() => {
      if (props.isSpecialRowHeader) {
        return { rows: props.rows };
      } else {
        const headerRow = {
          rowId: "header",
          height: heightRow,
          cells: props.columns.map((item: ColumnProps, idx) => {
            const filter: any = props.formik.values ?? {};
            const parsedColumnId = parseColumnId(
              toString(item?.columnId),
              props.additionalField,
            );

            const filterValue = get(filter, parsedColumnId);
            const isSearchActive = Boolean(
              filterValue &&
                typeof filterValue === "object" &&
                (typeof filterValue.search === "string"
                  ? filterValue.search.trim() !== ""
                  : filterValue.search),
            );
            const hasCheckboxFilter = Boolean(
              filterValue &&
                typeof filterValue === "object" &&
                !Array.isArray(filterValue) &&
                "filter" in filterValue &&
                filterValue.filter !== undefined,
            );
            const hasBlankFilter = Boolean(
              filterValue &&
                typeof filterValue === "object" &&
                !Array.isArray(filterValue) &&
                filterValue.filter_special_empty,
            );

            const isFilterAction =
              isSearchActive || hasCheckboxFilter || hasBlankFilter;
            const isActiveFilter = filterValue && isFilterAction;

            return {
              type: "headerIcon",
              text: item?.label || "",
              icon: item?.icon,
              index: idx,
              isActive: !!isActiveFilter,
              columnId: item?.columnId,
              width: props.columns?.find(
                (column) => column?.columnId === item?.columnId,
              )?.width,
              filter: filter,
              required:
                props.requiredFields?.includes(toString(item?.columnId)) ||
                item?.required,
              additionalField: props.additionalField,
              filterInfo: filterInfo,
              setFilterInfo: setFilterInfo,
              isShowTooltip: props?.isShowTooltip,
              tooltip: item?.tooltip || "",
              hiddenFilter: item.hiddenFilter,
              displaySize: displaySize,
            };
          }),
        };
        const groupHeaderRow = props?.rows?.find(
          (item: any) => item?.rowId === "group-header",
        );
        if (groupHeaderRow) {
          const otherRows = props?.rows?.filter(
            (item: any) => item?.rowId !== "group-header",
          );
          return {
            rows: [headerRow, ...otherRows],
            groupHeaderRow,
          };
        } else {
          if (props.rows[0]?.rowId === "header") {
            return {
              rows: [
                headerRow,
                ...combineRowWithHeightConfig(
                  props.rows.slice(1),
                  configRowHeights,
                ),
              ],
            };
          }
          return {
            rows: [
              headerRow,
              ...combineRowWithHeightConfig(props.rows, configRowHeights),
            ],
          };
        }
      }
    }, [
      props.columns,
      props.rows,
      props.formik,
      props.requiredFields,
      props.additionalField,
      props.isSpecialRowHeader,
      configRowHeights,
      displaySize,
    ]);
    useEffect(() => {
      const rowIds = rows
        .filter(
          (row) => row.rowId !== "header" && row.rowId !== "scroll-spacer-row",
        )
        .map((row) => row.rowId);
      handleCalHeightByRowIds(rowIds as string[]);
    }, [rows]);

    const handleCellClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;

      if (
        target.closest(".filter-wrapper") ||
        target.closest(".filter-header")
      ) {
        return;
      }

      if (filterInfo.position) {
        setFilterInfo({ open: false, columnId: "" });
      }
    };
    const handleScroll = useCallback(() => {
      if (filterInfo.position) {
        setFilterInfo({ open: false, columnId: "" });
      }
    }, [filterInfo.position]);

    const handleApplyFilter = () => {
      props.onFilter?.();
      setFilterInfo({
        open: false,
        columnId: "",
      });
    };

    const forceBlurGrid = useCallback(() => {
      const gridContainer = gridContainerRef.current;
      const activeElement = document.activeElement as HTMLElement | null;
      if (
        gridContainer &&
        activeElement &&
        gridContainer.contains(activeElement)
      ) {
        activeElement.blur();
      }
    }, []);

    const deactivateGrid = useCallback(() => {
      setRanges([]);
      setHighlightedRows([]);
      setFilterInfo({ open: false, columnId: "" });
      forceBlurGrid();
      // Remount ReactGrid to clear internal focused/editor state (Safari-safe).
    }, [forceBlurGrid]);

    const activateCurrentGrid = useCallback(() => {
      activateGridController({
        id: gridInstanceIdRef.current,
        deactivate: deactivateGrid,
      });
    }, [deactivateGrid]);

    useEffect(() => {
      return () => {
        clearGridController(gridInstanceIdRef.current);
      };
    }, []);

    useEffect(() => {
      const controller = new AbortController();

      const handleMouseUp = debounce((e: MouseEvent) => {
        const selectedRanges = ref.current?.state?.selectedRanges || [];
        if (selectedRanges.length) {
          setRanges(selectedRanges);
        } else {
          ranges.length && setRanges([]);
        }
      }, 100);

      if (ref.current) {
        window.addEventListener("mouseup", handleMouseUp, {
          signal: controller.signal,
        });
      }

      return () => {
        controller.abort();
        handleMouseUp.cancel();
      };
    }, [ref.current, ranges]);

    // Attach scroll event listener to ReactGrid's scrollable element
    useEffect(() => {
      const scrollableElement = ref.current?.state?.scrollableElement as
        | HTMLDivElement
        | undefined;

      if (scrollableElement) {
        const onScroll = (ev: Event) => {
          handleScroll();
          props.onTableScroll?.(ev as unknown as React.UIEvent<HTMLDivElement>);
        };

        scrollableElement.addEventListener("scroll", onScroll);
        return () => {
          scrollableElement.removeEventListener("scroll", onScroll);
        };
      }
    }, [ref.current, handleScroll, props.onTableScroll]);

    // Find shadow-top element after ReactGrid is rendered
    useEffect(() => {
      const findShadowTopElement = () => {
        if (ref.current) {
          const reactGridElement = ref.current as any;
          const shadowTop = reactGridElement?.querySelector?.(
            ".rg-pane-shadow.shadow-top",
          );
          if (shadowTop) {
            setShadowTopElement(shadowTop);
            shadowTop.style.zIndex = "10";
            return;
          }
        }
        // Try to find in the document if not found in ref
        const shadowTop = document.querySelector(
          ".rg-pane-shadow.shadow-top",
        ) as HTMLElement;
        if (shadowTop) {
          setShadowTopElement(shadowTop);
          shadowTop.style.zIndex = "10";
        }
      };

      // Try immediately
      findShadowTopElement();

      // Also try after a short delay to ensure ReactGrid has rendered
      const timeoutId = setTimeout(findShadowTopElement, 100);

      return () => {
        clearTimeout(timeoutId);
      };
    }, [ref.current, groupHeaderRow]);

    return (
      <div
        ref={(element) => {
          gridContainerRef.current = element;
          if (!props?.elementRef) return;
          (props.elementRef as any).current = element;
        }}
        className={props?.className ?? "table-main"}
        onMouseDownCapture={activateCurrentGrid}
        onFocusCapture={activateCurrentGrid}
        onScroll={(e) => {
          handleScroll();
          props.onTableScroll?.(e);
        }}
      >
        {props?.wrapContent && <Fragment>{props?.wrapContent}</Fragment>}
        <div
          className="table-container"
          style={props?.stylesTableContainer}
          onClick={handleCellClick}
        >
          {groupHeaderRow &&
            (shadowTopElement ? (
              createPortal(
                <HeaderGroup
                  groupHeaderRow={groupHeaderRow}
                  columns={props.columns}
                  stickyLeftColumns={props.stickyLeftColumns}
                />,
                shadowTopElement,
              )
            ) : (
              <HeaderGroup
                groupHeaderRow={groupHeaderRow}
                columns={props.columns}
                stickyLeftColumns={props.stickyLeftColumns}
              />
            ))}
          {props?.isShowTooltip &&
            (shadowTopElement ? (
              createPortal(
                <TooltipHeader columns={props.columns} />,
                shadowTopElement,
              )
            ) : (
              <TooltipHeader columns={props.columns} />
            ))}
          <Styles
            className={`not-set-height ${
              groupHeaderRow || addHeight ? "" : "ignore-sticky-top"
            }`}
          >
            <FilterHeader
              columns={props.columns}
              formik={props.formik}
              filterInfo={filterInfo}
              setFilterInfo={setFilterInfo}
              data={props?.data}
              originalData={props?.originalData}
              onFilter={handleApplyFilter}
              additionalField={props.additionalField}
              dateFieateHierarchyFields={props.dateFieateHierarchyFields}
              filterFields={props.filterFields}
              getValue={props.getValue}
              isPreferUseGetValue={props.isPreferUseGetValue}
              fetchFilterOptions={fetchFilterOptions}
            />

            <GridErrorBoundary>
              <ReactGrid
                key={props.columns.map((c: any) => c.columnId).join(",")}
                {...props}
                onCellsChanged={(cellChanges) => {
                  props?.onCellsChanged && props?.onCellsChanged(cellChanges);
                  handleCalHeightByCellChange(cellChanges);
                }}
                highlights={
                  highlightedRows.length ? highlightedRows : props.highlights
                }
                columns={props.columns}
                ref={ref}
                rows={rows}
                customCellTemplates={{
                  ...props?.customCellTemplates,
                  headerIcon: new HeaderIconCellTemplate(),
                }}
              />
            </GridErrorBoundary>

            <div
              className={`container-result__total ${
                (!isShow || isHiddenTotalRow) && "hidden"
              }`}
              style={{ ...style }}
            >
              <CellInfoSum
                isHidden={!isShow}
                infos={{
                  total: format(total.toNumber()),
                  avg: format(total.div(sums.countNumber).toNumber() || 0),
                  min: format(sums.min || 0),
                  max: format(sums.max || 0),
                  count: format(sums.count || 0),
                  countNumber: format(sums.countNumber || 0),
                  distinctCount: format(sums.distinctCount || 0),
                }}
              />
            </div>
          </Styles>
        </div>
        {props?.isCtrlHToReplacesValue && (
          <PanelCtrlH
            rows={props.rows}
            key={props.nameTable}
            columns={props.columns}
            handleChangeHighlight={(rows: any[]) => {
              setHighlightedRows(rows);
            }}
            onCellsChanged={props.onCellsChanged}
            scrollToCell={scrollToCell}
            nameTable={props.nameTable}
          />
        )}
      </div>
    );
  },
);

ReactGirdCustom.displayName = "ReactGridCustom";

export default ReactGirdCustom;
